import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import {
  detailPickerSelector,
  actions as detailPickerActions,
} from "reducers/detailPicker";
import {
  detailTransactionSelector,
  actions as detailTransactionActions,
} from "reducers/detailTransaction";
import { pickersSelector, actions as pickerActions } from "reducers/pickers";
import {
  preliquidationSelector,
  actions as preliActions,
} from "reducers/preliquidation";
import { regionSelector, actions as regionsActions } from "reducers/regions";
import { slotSelector, actions as slotsActions } from "reducers/slots";

const basicDataItems = [
  "accountType",
  "banks",
  "identifications",
  "invoiceTypes",
  "provinces",
  "regions",
  "slotStatus",
  "origins",
  "relationships",
] as const;

export type BasicDataItemsType = (typeof basicDataItems)[number];

const basicDataActions: { [key in BasicDataItemsType]: (...args: any[]) => any } = {
  accountType: detailPickerActions.getAccountTypeRequest,
  banks: detailPickerActions.getBanksRequest,
  identifications: pickerActions.getIdentificationTypesRequest,
  invoiceTypes: preliActions.getInvoiceDetailTypesRequest,
  provinces: detailPickerActions.getProvincesRequest,
  regions: regionsActions.getRegionsRequest,
  slotStatus: slotsActions.getStatusRequest,
  relationships: detailTransactionActions.getRelationshipsRequest,
  origins: (minSla?: string, maxSla?: string) =>
    regionsActions.getOriginsFetch({ minSla, maxSla }),
};

const useBasicData = (items: BasicDataItemsType[]) => {
  const dispatch = useDispatch();
  const { regions } = useSelector(regionSelector);
  const { identificationTypes } = useSelector(pickersSelector);
  const { status } = useSelector(slotSelector);
  const { provinces, accountTypes, banks } = useSelector(detailPickerSelector);
  const { invoiceTypes } = useSelector(preliquidationSelector);
  const { origins } = useSelector(regionSelector);
  const { relationships } = useSelector(detailTransactionSelector);

  const selectors = useMemo(
    () => ({
      accountType: accountTypes,
      banks: banks,
      identifications: identificationTypes,
      invoiceTypes: invoiceTypes,
      provinces: provinces,
      regions: regions,
      slotStatus: status,
      origins: origins,
      relationships: relationships,
    }),
    [
      accountTypes,
      banks,
      identificationTypes,
      invoiceTypes,
      provinces,
      regions,
      status,
      relationships,
      origins,
    ]
  );

  const { search } = useLocation();

  useEffect(() => {
    const queryParams = new URLSearchParams(search);

    const minSla = queryParams.get("minSla");
    const maxSla = queryParams.get("maxSla");

    items.forEach((item: BasicDataItemsType) => {
      if (!selectors[item] || selectors[item].length === 0) {
        if (minSla && maxSla) {
          dispatch(basicDataActions[item](minSla, maxSla));
        } else {
          dispatch(basicDataActions[item]());
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  return selectors;
};

export default useBasicData;
